import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import moment from 'moment';
import './PlanItem.css';
import Activity from './activities/Activity';
import { debounce } from 'lodash';
import Spinner from './Spinner';
import { fetchInstructionOptionsCached } from '../utils/instructionOptionsCache';
import { createSerialWriter } from './planItemWriteQueue';
import { ShoppingBagIcon, PhotoIcon, ChartBarIcon, ClipboardDocumentListIcon, EllipsisVerticalIcon, ChevronUpIcon, ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import DiagonalDumbbellIcon from './activities/DiagonalDumbbellIcon';

const PlanItem = ({ onUpdateItem, editingItem, defaultStartDay, planId, modalService, exercises, backendService, isRegenerating, onRemoveItem, highlighted = false, onActivityReady, isExpanded, onToggleExpand, readOnly = false, canMoveUp = false, canMoveDown = false, showMoveActions = true, weeklyAgenda = false, onMoveUp, onMoveDown, onError, onDirty, registerPendingCommit }) => {
  const [startDay, setStartDay] = useState(defaultStartDay);
  const [displayStartDay, setDisplayStartDay] = useState('');
  const [recurrenceNumber, setRecurrenceNumber] = useState('1');
  const [recurrenceUnit, setRecurrenceUnit] = useState('days');
  const [instruction, setInstruction] = useState('do_workout_generated_manually');
  const [userCanModify, setUserCanModify] = useState(true);
  const [autogenerationHint, setAutogenerationHint] = useState('');
  const [completionDays, setCompletionDays] = useState(1);
  const [activity, setActivity] = useState(null);
  const [isEditingRecurrence, setIsEditingRecurrence] = useState(false);
  const [isEditingInstruction, setisEditingInstruction] = useState(false);
  const [instructionDropdownOpen, setInstructionDropdownOpen] = useState(false);
  const [isEditingStartDate, setIsEditingStartDate] = useState(false);
  const [isEditingHint, setIsEditingHint] = useState(false);
  const [isEditingCompletionDays, setIsEditingCompletionDays] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [instructionOptions, setInstructionOptions] = useState([]);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const recurrenceInputRef = useRef(null);
  const startDateInputRef = useRef(null);
  const hintInputRef = useRef(null);
  const recurrenceContainerRef = useRef(null);
  const recurrenceControlsRef = useRef(null);
  const completionDaysInputRef = useRef(null);
  const itemRef = useRef(null);
  const instructionContainerRef = useRef(null);
  const instructionButtonRef = useRef(null);
  const actionMenuRef = useRef(null);
  const actionMenuButtonRef = useRef(null);
  const inFlightWritesRef = useRef(new Set());
  const lastWriteErrorRef = useRef(null);

  const activityId = useMemo(() => editingItem?.activity_id, [editingItem]);
  const editingItemId = editingItem?._id;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (instructionDropdownOpen && instructionContainerRef.current && !instructionContainerRef.current.contains(event.target)) {
        setisEditingInstruction(false);
        setInstructionDropdownOpen(false);
      }
      if (actionMenuOpen && actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActionMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [instructionDropdownOpen, actionMenuOpen]);

  useEffect(() => {
    if (!actionMenuOpen) return undefined;
    const frame = requestAnimationFrame(() => actionMenuRef.current?.querySelector('.plan-item-action-menu button:not(:disabled)')?.focus());
    return () => cancelAnimationFrame(frame);
  }, [actionMenuOpen]);

  useEffect(() => {
    if (!instructionDropdownOpen) return undefined;
    const frame = requestAnimationFrame(() => instructionContainerRef.current?.querySelector('.dropdown-item:not(:disabled)')?.focus());
    return () => cancelAnimationFrame(frame);
  }, [instructionDropdownOpen]);

  const getDefaultCompletionDays = (instruction) => {
    // Map instructions to types and then to default days
    const typeMap = {
      'do_workout_generated_manually': 'workout',
      'do_workout_generated_automatically_in_advance': 'workout',
      'do_workout_regenerated_every_time': 'workout',
      'eat_meal': 'meal',
      'upload_media': 'media',
      'take_measurements': 'metric',
      'take_survey': 'survey'
    };
    const activityType = typeMap[instruction];
    switch (activityType) {
      case 'workout':
        return 3;
      case 'meal':
        return 1;
      case 'media':
        return 7;
      case 'survey':
        return 1;
      default:
        return 1;
    }
  };

  const getInstructionIcon = (instruction) => {
    const typeMap = {
      'do_workout_generated_manually': 'workout',
      'do_workout_generated_automatically_in_advance': 'workout',
      'do_workout_regenerated_every_time': 'workout',
      'eat_meal': 'meal',
      'upload_media': 'media',
      'take_measurements': 'metric',
      'take_survey': 'survey'
    };
    const type = typeMap[instruction] || 'workout';
    
    switch (type) {
      case 'workout': return DiagonalDumbbellIcon;
      case 'meal': return ShoppingBagIcon;
      case 'media': return PhotoIcon;
      case 'metric': return ChartBarIcon;
      case 'survey': return ClipboardDocumentListIcon;
      default: return DiagonalDumbbellIcon;
    }
  };

  const isElementInViewport = useCallback((el) => {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
    );
  }, []);

  useEffect(() => {
    return () => {};
  }, [editingItem]);

  useEffect(() => {
    if (onActivityReady && editingItem?._id) {
      const hasActivity = !!activityId;
      if (!isExpanded || !hasActivity) {
        onActivityReady(editingItem._id);
      }
    }
  }, [onActivityReady, editingItem?._id, isExpanded, activityId]);

  useEffect(() => {
    if (editingItem) {
      let localMidnight = moment(editingItem.start_time, moment.ISO_8601, true).startOf('day');
      if (!localMidnight.isValid()) {
        localMidnight = moment().startOf('day');
      }
      setStartDay(localMidnight.utc().format('YYYY-MM-DD HH:mm:ss'));
      setDisplayStartDay(localMidnight.format('YYYY-MM-DD'));
      setRecurrenceNumber(editingItem.recurrence_number);
      setRecurrenceUnit(editingItem.recurrence_unit);
      setInstruction(editingItem.instruction);
      setUserCanModify(editingItem.user_can_modify);
      setAutogenerationHint(editingItem.autogeneration_hint || '');
      setCompletionDays(editingItem.completion_days || getDefaultCompletionDays(editingItem.instruction));
    } else {
      let localMidnight = moment(defaultStartDay, moment.ISO_8601, true).startOf('day');
      if (!localMidnight.isValid()) {
        localMidnight = moment().startOf('day');
      }
      setStartDay(localMidnight.utc().format('YYYY-MM-DD HH:mm:ss'));
      setDisplayStartDay(localMidnight.format('YYYY-MM-DD'));
      setRecurrenceNumber('1');
      setRecurrenceUnit('days');
      setInstruction('do_workout_generated_manually');
      setUserCanModify(true);
      setAutogenerationHint('');
      setCompletionDays(getDefaultCompletionDays('do_workout_generated_manually'));
    }
  }, [editingItem, defaultStartDay]);

  useEffect(() => {
    if (!editingItem) return;
    if (!activityId) {
      setActivity(null);
      return;
    }
    if (!isExpanded && !highlighted) {
      // Avoid fetching heavy activity details until the user expands.
      return;
    }

    const fetchActivity = async () => {
      try {
        const activityData = await backendService.fetchActivity(activityId);
        setActivity(activityData);
      } catch (error) {
        console.error('Failed to fetch activity:', error);
        onError?.('Could not load this activity’s details. Try collapsing and reopening it.');
        setActivity(null);
        if (onActivityReady && editingItem?._id) {
          onActivityReady(editingItem._id);
        }
      }
    };
    fetchActivity();
  }, [activityId, backendService, editingItem, isExpanded, highlighted, onActivityReady, onError]);

  useEffect(() => {
    if (isEditingRecurrence) {
      setTimeout(() => {
        if (recurrenceInputRef.current) {
          recurrenceInputRef.current.focus();
        }
      }, 0);
    }
  }, [isEditingRecurrence]);

  useEffect(() => {
    if (isEditingStartDate) {
      setTimeout(() => {
        if (startDateInputRef.current) {
          startDateInputRef.current.focus();
        }
      }, 0);
    }
  }, [isEditingStartDate]);

  useEffect(() => {
    if (isEditingCompletionDays) {
      setTimeout(() => {
        if (completionDaysInputRef.current) {
          completionDaysInputRef.current.focus();
        }
      }, 0);
    }
  }, [isEditingCompletionDays]);

  const adjustHintHeight = useCallback(() => {
    if (hintInputRef.current) {
      hintInputRef.current.style.height = 'auto';
      hintInputRef.current.style.height = `${hintInputRef.current.scrollHeight}px`;
    }
  }, []);

  useEffect(() => {
    if (isEditingHint) {
      setTimeout(() => {
        if (hintInputRef.current) {
          hintInputRef.current.focus();
          adjustHintHeight();
        }
      }, 0);
    }
  }, [isEditingHint, adjustHintHeight]);

  const trackWrite = useCallback((promise) => {
    lastWriteErrorRef.current = null;
    inFlightWritesRef.current.add(promise);
    promise.then(
      () => inFlightWritesRef.current.delete(promise),
      error => {
        lastWriteErrorRef.current = error;
        inFlightWritesRef.current.delete(promise);
      },
    );
    return promise;
  }, []);

  const itemWriter = useMemo(() => createSerialWriter(async (updates) => {
    const response = await backendService.savePlanItem(planId, editingItemId || null, updates);
    onUpdateItem(response);
    return response;
  }), [backendService, planId, editingItemId, onUpdateItem]);

  const debouncedSavePlanItem = useMemo(
    () => debounce(async (updates) => {
      try {
        await trackWrite(itemWriter.enqueue(updates));
      } catch (error) {
        console.error('Failed to save item:', error);
        onError?.('Could not save the activity changes. Your current values remain visible so you can retry.');
      }
    }, 500),
    [itemWriter, onError, trackWrite]
  );

  useEffect(() => {
    const entryId = editingItemId;
    const controller = {
      flush: async () => {
        const flushed = debouncedSavePlanItem.flush();
        if (flushed && typeof flushed.then === 'function') await flushed;
        await itemWriter.flush();
        await Promise.allSettled([...inFlightWritesRef.current]);
        if (lastWriteErrorRef.current) throw lastWriteErrorRef.current;
      },
      cancel: async () => {
        debouncedSavePlanItem.cancel();
        await itemWriter.flush().catch(() => {});
        await Promise.allSettled([...inFlightWritesRef.current]);
      },
    };
    if (entryId) registerPendingCommit?.(entryId, controller);
    // The parent owns controller lifetime so its unmount fallback can still
    // flush a final debounced edit after child effects have been cleaned up.
    return undefined;
  }, [debouncedSavePlanItem, editingItemId, itemWriter, registerPendingCommit]);

  const commitChanges = useCallback(async () => {
    if (readOnly || Object.keys(pendingChanges).length === 0) return;
    try {
      const flushed = debouncedSavePlanItem.flush();
      if (flushed && typeof flushed.then === 'function') await flushed;
      await itemWriter.flush();
      await Promise.allSettled([...inFlightWritesRef.current]);
      if (lastWriteErrorRef.current) throw lastWriteErrorRef.current;
      setPendingChanges({});
      if (itemRef.current && !isElementInViewport(itemRef.current)) {
        itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.error('Failed to save item:', error);
      onError?.('Could not save the activity changes. Your current values remain visible so you can retry.');
    }
  }, [debouncedSavePlanItem, itemWriter, pendingChanges, isElementInViewport, readOnly, onError]);

  const handleFieldChange = (field, value) => {
    if (readOnly) return;
    if (field === 'instruction') {
      setInstruction(value);
    }
    if (field === 'start_time') {
      const localMidnight = moment(value, 'YYYY-MM-DD', true).startOf('day');
      if (!localMidnight.isValid()) {
        return;
      }
      onDirty?.();
      setStartDay(localMidnight);
      setDisplayStartDay(value);
      setPendingChanges(prev => ({ ...prev, start_time: localMidnight }));
      debouncedSavePlanItem({
        ...pendingChanges,
        start_time: localMidnight,
      });
      return;
    }
    onDirty?.();
    if (field === 'recurrence_number') setRecurrenceNumber(value);
    if (field === 'recurrence_unit') setRecurrenceUnit(value);
    if (field === 'instruction') setInstruction(value);
    if (field === 'user_can_modify') setUserCanModify(value);
    if (field === 'autogeneration_hint') setAutogenerationHint(value);
    if (field === 'completion_days') setCompletionDays(value);

    setPendingChanges(prev => ({ ...prev, [field]: value }));

    debouncedSavePlanItem({
      start_time: field === 'start_time' ? value : startDay,
      recurrence_number: field === 'recurrence_number' ? value : recurrenceNumber,
      recurrence_unit: field === 'recurrence_unit' ? value : recurrenceUnit,
      instruction: field === 'instruction' ? value : instruction,
      user_can_modify: field === 'user_can_modify' ? value : userCanModify,
      autogeneration_hint: field === 'autogeneration_hint' ? value : autogenerationHint,
      completion_days: field === 'completion_days' ? value : completionDays,
    });
  };

  const handleRecurrenceDisplayClick = () => {
    setIsEditingRecurrence(true);
  };

  const handleRecurrenceBlur = (e) => {
    if (!recurrenceContainerRef.current?.contains(e.relatedTarget)) {
      setIsEditingRecurrence(false);
    }
  };

  const handleInstructionDisplayClick = () => {
    setisEditingInstruction(true);
    setInstructionDropdownOpen(true);
  };

  const handleStartDateDisplayClick = () => {
    setIsEditingStartDate(true);
  };

  const handleStartDateBlur = () => {
    setIsEditingStartDate(false);
    commitChanges();
  };

  const handleHintDisplayClick = () => {
    setIsEditingHint(true);
  };

  const handleHintBlur = () => {
    setIsEditingHint(false);
  };

  const handleCompletionDaysDisplayClick = () => {
    setIsEditingCompletionDays(true);
  };

  const handleCompletionDaysBlur = () => {
    setIsEditingCompletionDays(false);
  };

  const formatRecurrenceFrequency = () => {
    const startDate = moment(startDay, 'YYYY-MM-DD HH:mm:ss', true).local();
    if (!startDate.isValid()) {
      return 'Every day';
    }
    const num = parseInt(recurrenceNumber, 10);

    if (recurrenceUnit === 'weeks') {
      if (num === 1) {
        return `Every ${startDate.format('dddd')}`;
      }
      return `Every ${num} ${startDate.format('dddd')}s`;
    } else if (recurrenceUnit === 'months') {
      if (num === 1) {
        return `Every ${startDate.format('Do')} of the month`;
      }
      return `Every ${num} months on the ${startDate.format('Do')}`;
    } else if (recurrenceUnit === 'days') {
      if (num === 1) {
        return 'Every day';
      }
      return `Every ${num} days`;
    }

    return `Every ${num} ${recurrenceUnit}`;
  };

  const formatRecurrenceStartDate = () => {
    const startDate = moment(startDay, 'YYYY-MM-DD HH:mm:ss', true).local();
    if (!startDate.isValid()) {
      return 'Starting today';
    }
    const formattedDate = startDate.format('MMMM Do YYYY');
    return `Starting on ${formattedDate}`;
  };

  const formatInstruction = () => {
    const option = instructionOptions?.[instruction];
    return option ? option.display : '';
  };

  const formatAutogenerationHint = () => {
    return autogenerationHint || 'Click to give generation instructions';
  };

  useEffect(() => {
    const loadInstructionOptions = async () => {
      try {
        const options = await fetchInstructionOptionsCached(backendService, planId);
        setInstructionOptions(options);
      } catch (error) {
        console.error('Failed to load instruction options:', error);
      }
    };
    loadInstructionOptions();
  }, [backendService, planId]);

  const entryClassName = [
    'plan-item-entry',
    highlighted ? 'plan-item-entry-highlighted' : '',
    isExpanded ? 'is-expanded' : '',
  ].filter(Boolean).join(' ');

  const instructionDisplay = formatInstruction() || 'Plan activity';
  const itemTitle = editingItem?.activity_name || editingItem?.name || instructionDisplay;
  const recurrenceDisplay = formatRecurrenceFrequency();
  const recurrenceSummary = weeklyAgenda && recurrenceUnit === 'weeks'
    ? (Number(recurrenceNumber) === 1 ? 'Every week' : `Every ${recurrenceNumber} weeks`)
    : recurrenceDisplay;
  const startDateDisplay = formatRecurrenceStartDate();
  const IconComponent = getInstructionIcon(instruction);
  const configRegionId = `plan-item-config-${editingItem?._id || 'new'}`;

  return (
    <div className={entryClassName} ref={itemRef}>
      <div className="plan-item-header">
        <button
          type="button"
          className="plan-item-disclosure-button"
          onClick={onToggleExpand}
          aria-expanded={isExpanded}
          aria-controls={configRegionId}
        >
          <span className="plan-item-icon-col">
            <IconComponent className="plan-item-icon" />
          </span>
          <span className="plan-item-content-col">
            <span className="plan-item-line-1">{itemTitle}</span>
            <span className="plan-item-line-2">
              <span>{recurrenceSummary} · {completionDays}-day window</span>
              {instructionOptions?.[instruction]?.is_autogen_supported && autogenerationHint && (
                <span className="plan-item-hint-text">{autogenerationHint}</span>
              )}
            </span>
          </span>
        </button>
        <div
          className="plan-item-actions-col"
          ref={actionMenuRef}
          onKeyDown={event => {
            if (event.key === 'Escape' && actionMenuOpen) {
              event.stopPropagation();
              setActionMenuOpen(false);
              actionMenuButtonRef.current?.focus();
            }
          }}
        >
          {readOnly ? (
            <span className={`plan-item-disclosure ${isExpanded ? 'is-open' : ''}`} aria-hidden="true">›</span>
          ) : (
            <>
              <button
                type="button"
                ref={actionMenuButtonRef}
                className="plan-item-menu-trigger"
                onClick={(event) => {
                  event.stopPropagation();
                  setActionMenuOpen(open => !open);
                }}
                disabled={isRegenerating}
                aria-label={`Actions for ${itemTitle}`}
                aria-controls={`plan-item-actions-${editingItem?._id || 'new'}`}
                aria-expanded={actionMenuOpen}
              >
                <EllipsisVerticalIcon aria-hidden="true" />
              </button>
              {actionMenuOpen && (
                <div id={`plan-item-actions-${editingItem?._id || 'new'}`} className="plan-item-action-menu" role="group" aria-label={`Reorder or delete ${itemTitle}`} onClick={event => event.stopPropagation()}>
                  {showMoveActions && (
                    <>
                      <button type="button" disabled={!canMoveUp || isRegenerating} onClick={() => { setActionMenuOpen(false); onMoveUp?.(); requestAnimationFrame(() => actionMenuButtonRef.current?.focus()); }}>
                        <ChevronUpIcon aria-hidden="true" /> Move earlier
                      </button>
                      <button type="button" disabled={!canMoveDown || isRegenerating} onClick={() => { setActionMenuOpen(false); onMoveDown?.(); requestAnimationFrame(() => actionMenuButtonRef.current?.focus()); }}>
                        <ChevronDownIcon aria-hidden="true" /> Move later
                      </button>
                    </>
                  )}
                  <button type="button" className="danger" disabled={isRegenerating} onClick={() => { setActionMenuOpen(false); onRemoveItem(); requestAnimationFrame(() => actionMenuButtonRef.current?.focus()); }}>
                    <TrashIcon aria-hidden="true" /> Delete activity
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isExpanded && (
        <>
          <div id={configRegionId} className="plan-item-config" role="region" aria-label={`${itemTitle} settings`}>
            <div className="config-section instruction-section" ref={instructionContainerRef}>
              <label className="config-label" htmlFor={`${configRegionId}-instruction`}>Instruction</label>
              <div className="config-value-container">
                {isEditingInstruction && instructionDropdownOpen ? (
                  <div className="custom-dropdown" onKeyDown={event => {
                    if (event.key === 'Escape') {
                      event.stopPropagation();
                      setisEditingInstruction(false);
                      setInstructionDropdownOpen(false);
                      requestAnimationFrame(() => instructionButtonRef.current?.focus());
                    }
                  }}>
                    {Object.entries(instructionOptions || {}).map(([value, opt]) => (
                      <button
                        type="button"
                        key={value}
                        className="dropdown-item"
                        onClick={() => {
                          handleFieldChange('instruction', value);
                          setisEditingInstruction(false);
                          setInstructionDropdownOpen(false);
                          requestAnimationFrame(() => instructionButtonRef.current?.focus());
                        }}
                      >
                        {opt.display}
                      </button>
                    ))}
                  </div>
                ) : (
                  <button
                    type="button"
                    id={`${configRegionId}-instruction`}
                    ref={instructionButtonRef}
                    className="config-value instruction-display"
                    onClick={handleInstructionDisplayClick}
                    disabled={readOnly}
                    aria-label={`Instruction: ${instructionDisplay}`}
                  >
                    {instructionDisplay}
                  </button>
                )}
              </div>
            </div>

            <div className="config-grid">
              <div className="config-item">
                <label className="config-label" htmlFor={`${configRegionId}-frequency`}>Frequency</label>
                <div className="config-value-container" ref={recurrenceControlsRef}>
                  {isEditingRecurrence ? (
                    <div
                      className="recurrence-edit-mode"
                      ref={recurrenceContainerRef}
                      onBlur={handleRecurrenceBlur}
                    >
                      <span className="prefix">Every</span>
                      <input
                        ref={recurrenceInputRef}
                        id={`${configRegionId}-frequency`}
                        type="number"
                        className="small-input"
                        value={recurrenceNumber}
                        onChange={(e) => handleFieldChange('recurrence_number', e.target.value)}
                        placeholder="1"
                      />
                      <select
                        aria-label="Frequency unit"
                        value={recurrenceUnit}
                        onChange={(e) => handleFieldChange('recurrence_unit', e.target.value)}
                      >
                        <option value="days">days</option>
                        <option value="weeks">weeks</option>
                        <option value="months">months</option>
                      </select>
                    </div>
                  ) : (
                    <button id={`${configRegionId}-frequency`} type="button" className="config-value" onClick={handleRecurrenceDisplayClick} disabled={readOnly} aria-label={`Frequency: ${recurrenceDisplay}`}>
                      {recurrenceDisplay}
                    </button>
                  )}
                </div>
              </div>

              <div className="config-item">
                <label className="config-label" htmlFor={`${configRegionId}-start-date`}>Start Date</label>
                <div className="config-value-container">
                  {isEditingStartDate ? (
                    <div className="date-edit-mode" onBlur={handleStartDateBlur}>
                      <input
                        ref={startDateInputRef}
                        id={`${configRegionId}-start-date`}
                        type="date"
                        value={displayStartDay}
                        onChange={(e) => handleFieldChange('start_time', e.target.value)}
                        placeholder="Start Day"
                      />
                    </div>
                  ) : (
                    <button id={`${configRegionId}-start-date`} type="button" className="config-value" onClick={handleStartDateDisplayClick} disabled={readOnly} aria-label={`Start date: ${startDateDisplay}`}>
                      {startDateDisplay}
                    </button>
                  )}
                </div>
              </div>

              <div className="config-item">
                <label className="config-label" htmlFor={`${configRegionId}-completion-window`}>Completion Window</label>
                <div className="config-value-container">
                  {isEditingCompletionDays ? (
                    <div className="completion-edit-mode" onBlur={handleCompletionDaysBlur}>
                      <input
                        ref={completionDaysInputRef}
                        id={`${configRegionId}-completion-window`}
                        type="number"
                        className="small-input"
                        value={completionDays}
                        onChange={(e) => handleFieldChange('completion_days', parseInt(e.target.value) || 1)}
                        placeholder="1"
                        min="1"
                      />
                      <span className="suffix">days</span>
                    </div>
                  ) : (
                    <button id={`${configRegionId}-completion-window`} type="button" className="config-value" onClick={handleCompletionDaysDisplayClick} disabled={readOnly} aria-label={`Completion window: ${completionDays} day${completionDays !== 1 ? 's' : ''}`}>
                      {completionDays} day{completionDays !== 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>

              <div className="config-item checkbox-item">
                <label className="config-label" htmlFor={`${configRegionId}-user-modify`}>User Can Modify</label>
                <div className="config-value-container">
                  <label className="toggle-switch">
                    <input
                      id={`${configRegionId}-user-modify`}
                      type="checkbox"
                      aria-label="User can modify this activity"
                      checked={userCanModify}
                      disabled={readOnly}
                      onChange={(e) => handleFieldChange('user_can_modify', e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>
            </div>

            {instructionOptions?.[instruction]?.is_autogen_supported && (
              <div className="config-section hint-section">
                <label className="config-label" htmlFor={`${configRegionId}-generation-hint`}>Generation Instructions</label>
                <div className="config-value-container">
                  {isEditingHint ? (
                    <textarea
                      ref={hintInputRef}
                      id={`${configRegionId}-generation-hint`}
                      value={autogenerationHint}
                      onChange={(e) => handleFieldChange('autogeneration_hint', e.target.value)}
                      onBlur={handleHintBlur}
                      onInput={adjustHintHeight}
                      placeholder="Enter instructions for workout generation"
                      className="hint-input"
                    />
                  ) : (
                    <button id={`${configRegionId}-generation-hint`} type="button" className="config-value hint-display" onClick={handleHintDisplayClick} disabled={readOnly} aria-label={`Generation instructions: ${formatAutogenerationHint()}`}>
                      {formatAutogenerationHint()}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          {(isRegenerating && instructionOptions?.[instruction] && instructionOptions?.[instruction].is_autogen_supported) ? (
            <div className="spinner-container">
              <Spinner />
            </div>
          ) : (
            activity && (
              <div className="plan-item">
                <Activity
                  activity={activity}
                  exercises={exercises}
                  modalService={modalService}
                  backendService={backendService}
                  onActivityComplete={() => {}}
                  onActivityCancel={() => {}}
                  onReady={() => {
                    if (onActivityReady && editingItem?._id) {
                      onActivityReady(editingItem._id);
                    }
                  }}
                />
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default PlanItem;
